"use client";
import { Box, Card, CardActionArea, Grid, Icon, Skeleton, Stack, Typography } from "@mui/material";
import CarouselHeader from "@/app/(main)/_components/MainPage/CarouselHeader";
import Category2BoldIcon from "@/app/_components/Icons/Category2BoldIcon";
import { ItemType } from "../CategoriesSlider";
import useEmblaCarousel from "embla-carousel-react";
import React, { PropsWithChildren, useCallback, useEffect, useState } from "react";
import DOMPurify from "isomorphic-dompurify";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useGqlClient } from "@/Providers/GQLProvider";
import ProductCard from "../SITESEARCH/ProductCard";
import ProductCardSkeleton from "../SITESEARCH/ProductCardSkeleton";
import CategoriesSliderChips from "../CategoriesSliderChips";
import Image from "next/image";
import CarouselShowMoreCard from "../CarouselShowMoreCard";
import CarouselFadeSides from "./CarouselFadeSides";

function CatItem({ item, active, onChange }: { item: ItemType; active?: boolean; onChange: () => void }) {
  const iconSize = {
    xs: "20px !important",
    md: "30px !important",
  };

  return (
    <Box>
      <Card
        elevation={0}
        sx={{
          borderRadius: 200,
          bgcolor: active ? "grey.900" : "transparent",
          color: active ? "white.main" : "gray.800",
          userSelect: "none",
        }}
      >
        <CardActionArea
          onClick={onChange}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,
            p: 0.8,
            pr: 1.5,
          }}
        >
          {(item.image || item.svg) && (
            <Box
              sx={{
                position: "relative",
                width: 40,
                height: 40,
                flexShrink: 0,
                overflow: "hidden",
                borderRadius: "50%",
                backgroundColor: item.bgColor ?? "grey.50",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {item.image ? (
                <Image
                  src={item.image}
                  alt={item.title}
                  fill
                  draggable={false}
                  sizes="40px"
                  style={{
                    objectFit: "contain",
                    userSelect: "none",
                    pointerEvents: "none",
                  }}
                />
              ) : item.svg ? (
                <Icon
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: iconSize,
                    height: iconSize,
                    "& svg": {
                      width: iconSize,
                      height: iconSize,
                    },
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(item.svg),
                  }}
                />
              ) : null}
            </Box>
          )}

          <Typography>{item.title}</Typography>
        </CardActionArea>
      </Card>
    </Box>
  );
}

function arePropsEqual<P>(prevProps: Readonly<P>, nextProps: Readonly<P>): boolean {
  return JSON.stringify(prevProps) === JSON.stringify(nextProps);
}

function ProductWithCategoryFilterSection({ items }: { items?: ItemType[] }) {
  const gqlClient = useGqlClient();

  const [cat, setCat] = useState<ItemType | null>(items && items.length > 0 ? items[0] : null);
  const { data, isFetching } = useQuery({
    queryKey: [gqlClient.SearchByCategoryId.name, Number(cat?.id)],
    enabled: Boolean(cat?.id),
    placeholderData: keepPreviousData,
    queryFn: () => {
      return gqlClient.SearchByCategoryId({ searchByCategoryId: cat?.id as number });
    },
  });

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, direction: "rtl", dragFree: true, align: "start" });
  useEffect(() => {
    if (emblaApi && cat) {
      // emblaApi.reInit(); // Re-initialize Embla only when necessary
      emblaApi.scrollTo(0, true);
    }
  }, [emblaApi, cat]);

  const slideSpacing = "1rem";
  const slideSize = "95%";
  const slideSpacingXs = "0.5rem";
  const slideSizeXs = "calc(92% / 2)";
  const slideSpacingSm = "0.7rem";
  const slideSizeSm = "calc(90% / 3)";
  const slideSpacingMd = "0.8rem";
  const slideSizeMd = "calc(90% / 3)";
  const slideSpacingLg = "0.9rem";
  const slideSizeLg = "calc(95% / 4)";
  const slideSpacingXl = "1rem";
  const slideSizeXl = "calc(95% / 4)";
  const NotFountCard = useCallback(() => {
    return (
      <Card
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 2,
          gap: 1,
          minWidth: 0,
          width: "100%",
          background: "transparent",
          // flex: {
          //   xs: `0 0 ${slideSize}`,
          // },
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: { xs: 250, md: 320 },
            aspectRatio: 1,
          }}
        >
          <Image unoptimized src={"/errors/sadBasket.svg"} fill alt={"sadBasket"} style={{ objectFit: "contain" }} />
        </Box>
        <Typography variant="body2">محصولی پیدا نشد</Typography>
      </Card>
    );
  }, []);
  const CardBox = useCallback(({ children }: PropsWithChildren) => {
    return (
      <Box
        sx={{
          minWidth: 0,
          flex: {
            xs: `0 0 ${slideSizeXs}`,
            sm: `0 0 ${slideSizeSm}`,
            md: `0 0 ${slideSizeMd}`,
            lg: `0 0 ${slideSizeLg}`,
            xl: `0 0 ${slideSizeXl}`,
          },
          pr: {
            xs: `${slideSpacingXs}`,
            sm: `${slideSpacingSm}`,
            md: `${slideSpacingMd}`,
            lg: `${slideSpacingLg}`,
            xl: `${slideSpacingXl}`,
          },
        }}
      >
        {children}
      </Box>
    );
  }, []);
  return (
    <>
      <Stack mt={5}>
        <CarouselHeader
          title={"<b>جدیدترین </b> محصولات"}
          href={`${cat?.href}?sort=new`}
          icon={<Category2BoldIcon />}
          // @ts-ignore
          emblaApi={emblaApi}
        />
        <Grid container spacing={2}>
          <Grid item xs={12} md={4} lg={3} sx={{ display: { xs: "flex", md: "none" } }}>
            <CategoriesSliderChips
              items={items}
              cat={cat}
              setCat={(e) => {
                setCat(e);
              }}
            />
          </Grid>

          <Grid item xs={12} md={3.5} lg={3} sx={{ display: { xs: "none", md: "flex" } }} position={"relative"}>
            <Box
              className="custom-scrollbar"
              sx={{ position: "absolute", left: 0, right: 0, top: 0, bottom: 0, overflow: "auto", mt: 3, mb: 1, ml: 2, pr: 1 }}
            >
              <Stack spacing={1}>
                {items &&
                  items.map((item, index) => {
                    return (
                      <CatItem
                        key={index}
                        item={item}
                        onChange={() => {
                          setCat(item);
                        }}
                        active={item.id === cat?.id}
                      />
                    );
                  })}
              </Stack>
            </Box>
          </Grid>
          <Grid item xs={12} md={8.5} lg={9} sx={{ minHeight: { xs: "auto", md: 350, lg: 420 } }}>
            {/* embla */}
            <Box width={"100%"} sx={{ position: "relative" }}>
              {/*  embla__viewport */}
              <Box sx={{ overflow: "hidden", position: "relative" }} ref={emblaRef}>
                {/* embla__container */}
                <Box
                  sx={{
                    backfaceVisibility: "hidden",
                    display: "flex",
                    touchAction: "pan-y pinch-zoom",
                    py: 0.2,
                    mr: {
                      xs: data && data.products.length > 0 ? `calc(${slideSpacingXs} * -1)` : 0,
                      sm: data && data.products.length > 0 ? `calc(${slideSpacingSm} * -1)` : 0,
                      md: data && data.products.length > 0 ? `calc(${slideSpacingMd} * -1)` : 0,
                      lg: data && data.products.length > 0 ? `calc(${slideSpacingLg} * -1)` : 0,
                      xl: data && data.products.length > 0 ? `calc(${slideSpacingXl} * -1)` : 0,
                    },
                  }}
                >
                  {isFetching &&
                    Array.from({ length: 5 }).map((_, i) => {
                      return (
                        <CardBox key={i}>
                          <ProductCardSkeleton />
                        </CardBox>
                      );
                    })}
                  {data && data.products.length <= 0 && <NotFountCard />}
                  {data && data.products.length > 0 && (
                    <>
                      {data.products.map((product, index) => {
                        return (
                          // embla__slide
                          <CardBox key={product.id}>
                            <ProductCard product={product as any} surface="home_category" position={index} />
                          </CardBox>
                        );
                      })}
                      <CardBox key={data.products.length}>
                        <CarouselShowMoreCard svgIcon={cat?.svg} href={`${cat?.href}?sort=new`} />
                      </CardBox>
                    </>
                  )}
                </Box>
              </Box>
              <CarouselFadeSides emblaApi={emblaApi as any} />
            </Box>
          </Grid>
        </Grid>
      </Stack>
    </>
  );
}

export default React.memo(ProductWithCategoryFilterSection, arePropsEqual);
